/*
 * <beni-catbar-widget> —— 博客/资讯分类栏行为封装 (web component)
 *
 * 承担: sticky checkStuck (rAF 节流) + 移动端 openMenu/closeMenu + body scrim +
 *      锁背景滚动 (走 beni-scroll-lock refcount) + Escape/外点关闭 + 可选 tab prefetch.
 *
 * 用法 (blog):
 *   <beni-catbar-widget owner="blog-catbar-{{ section.id }}" scrim-class="beni-catbar-scrim">
 *     <div class="beni-catbar">
 *       <button data-catbar-toggle>...</button>
 *       <div data-catbar-menu>
 *         <div data-catbar-menu-inner>...</div>
 *       </div>
 *     </div>
 *   </beni-catbar-widget>
 *
 * 用法 (news, 加 prefetch):
 *   <beni-catbar-widget prefetch owner="news-catbar-{{ section.id }}" scrim-class="beni-news-catbar-scrim">
 *
 * 硬约束: 必须放在 .shopify-section 容器内 (Shopify 主题渲染默认满足).
 *
 * DOM 契约:
 *   [data-catbar-toggle]      —— 移动端切换按钮
 *   [data-catbar-menu]        —— 面板容器 (JS 控 max-height 缓动)
 *   [data-catbar-menu-inner]  —— 面板内容 (JS 读 scrollHeight 定位缓动目标)
 *
 * 生命周期: connectedCallback 挂监听, disconnectedCallback 全解绑 + 释放 scroll lock.
 * shopify:section:load/unload 会替换 DOM → 自动触发断连/重连.
 */
(function () {
  if (window.customElements && customElements.get('beni-catbar-widget')) return;

  class BeniCatbarWidget extends HTMLElement {
    connectedCallback() {
      // 必须在 shopify-section 内; fallback 到 this 会让状态类挂错元素 → CSS 失效
      this.section = this.closest('.shopify-section');
      if (!this.section) {
        console.warn('[beni-catbar-widget] must be inside .shopify-section, skipping init');
        return;
      }

      this.toggle = this.querySelector('[data-catbar-toggle]');
      this.menu = this.querySelector('[data-catbar-menu]');
      this.menuInner = this.querySelector('[data-catbar-menu-inner]');
      // 找不到关键 DOM 也放弃 (但静默, 编辑器初始态可能 DOM 未就绪)
      if (!this.toggle || !this.menu || !this.menuInner) return;

      this._scrimClass = this.getAttribute('scrim-class') || 'beni-catbar-scrim';
      this._prefetchOn = this.hasAttribute('prefetch');
      // owner 用于 scroll-lock refcount + 幂等去重, 未设则用元素引用地址
      this._owner = this.getAttribute('owner') || ('beni-catbar-' + Math.random().toString(36).slice(2, 9));
      this._raf = null;
      this._open = false;
      this._scrim = null;
      this._prefetched = Object.create(null);

      // sticky measure this.section (不是 this) —— .shopify-section 才是真正 position:sticky 的元素
      this._onScroll = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => this._checkStuck());
      };
      this._onKey = (e) => { if (e.key === 'Escape') this._close(); };
      this._onDoc = (e) => { if (!this.section.contains(e.target)) this._close(); };
      this._onToggle = (e) => {
        e.preventDefault();
        this._open ? this._close() : this._openMenu();
      };
      this._onIntent = (e) => this._prefetchHandler(e);

      addEventListener('scroll', this._onScroll, { passive: true });
      addEventListener('resize', this._onScroll, { passive: true });
      this.toggle.addEventListener('click', this._onToggle);

      if (this._prefetchOn) {
        var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        this._lowData = !!(conn && (conn.saveData || /2g/.test(conn.effectiveType || '')));
        this.addEventListener('mouseover', this._onIntent, { passive: true });
        this.addEventListener('touchstart', this._onIntent, { passive: true });
        this.addEventListener('focusin', this._onIntent);
      }

      this._checkStuck();
    }

    disconnectedCallback() {
      removeEventListener('scroll', this._onScroll);
      removeEventListener('resize', this._onScroll);
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this.section) this.section.classList.remove('is-stuck', 'is-menu-open');
      if (this.toggle) this.toggle.removeEventListener('click', this._onToggle);
      document.removeEventListener('click', this._onDoc, true);
      document.removeEventListener('keydown', this._onKey);
      if (this._prefetchOn) {
        this.removeEventListener('mouseover', this._onIntent);
        this.removeEventListener('touchstart', this._onIntent);
        this.removeEventListener('focusin', this._onIntent);
      }
      if (this._scrim) { this._scrim.remove(); this._scrim = null; }
      // 释放 scroll lock (无论此刻是否持有, release 幂等)
      if (window.__beniScrollLock) window.__beniScrollLock.release(this._owner);
    }

    _checkStuck() {
      this._raf = null;
      // 吸附阈值 = 元素解析后的 sticky top（自适应 header 显隐：header 可见时
      // top=公告栏+header 高，收起时仅公告栏；比只读公告栏变量更稳）。
      var dock = parseFloat(getComputedStyle(this.section).top) || 0;
      var stuck = this.section.getBoundingClientRect().top <= dock + 1;
      this.section.classList.toggle('is-stuck', stuck);
    }

    _openMenu() {
      this.menu.style.maxHeight = this.menuInner.scrollHeight + 'px';
      this.menu.classList.add('is-open');
      this.section.classList.add('is-menu-open');
      this.toggle.setAttribute('aria-expanded', 'true');

      if (!this._scrim) {
        this._scrim = document.createElement('div');
        this._scrim.className = this._scrimClass;
        this._scrim.appendChild(document.createElement('span')); // base.css div:empty 陷阱
        document.body.appendChild(this._scrim);
      }
      // scrim 从 bar 下沿铺到底: 用 catbar 元素本身(this)的 bottom
      var rect = this.getBoundingClientRect();
      this._scrim.style.top = Math.round(rect.bottom) + 'px';
      void this._scrim.offsetWidth;
      this._scrim.classList.add('is-visible');

      if (window.__beniScrollLock) window.__beniScrollLock.acquire(this._owner);

      document.addEventListener('click', this._onDoc, true);
      document.addEventListener('keydown', this._onKey);
      this._open = true;
    }

    _close() {
      if (!this._open) return;
      this.menu.style.maxHeight = '0';
      this.menu.classList.remove('is-open');
      this.section.classList.remove('is-menu-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      if (this._scrim) this._scrim.classList.remove('is-visible');
      if (window.__beniScrollLock) window.__beniScrollLock.release(this._owner);
      document.removeEventListener('click', this._onDoc, true);
      document.removeEventListener('keydown', this._onKey);
      this._open = false;
    }

    _prefetchHandler(e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a || !this.contains(a)) return;
      var href = a.getAttribute('href');
      if (!href || /^(https?:)?\/\//i.test(href)) return;
      if (this._prefetched[href] || this._lowData) return;
      this._prefetched[href] = true;
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'document';
      link.href = href;
      document.head.appendChild(link);
    }
  }

  customElements.define('beni-catbar-widget', BeniCatbarWidget);
})();
